// src/pages/ResetPassword.tsx
import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { GlobalStyles } from "../styles/globalStyles";
import {
  Mail,
  Lock,
  Eye,
  EyeOff,
  CheckCircle,
  ArrowLeft,
  Shield,
  Sparkles,
} from "lucide-react";
import logo from "../assets/images/logo.png";
import toast from "react-hot-toast";
import { authService } from "../services/api";

const ResetPassword: React.FC = () => {
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);

  const locationEmail = location.state?.email;

  useEffect(() => {
    inputRefs.current = inputRefs.current.slice(0, 6);
  }, []);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [countdown]);

  useEffect(() => {
    if (locationEmail) {
      setEmail(locationEmail);
    }
  }, [locationEmail]);

  const handleOtpChange = (index: number, value: string) => {
    if (value.length <= 1 && /^\d*$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);

      if (value && index < 5) {
        const nextInput = inputRefs.current[index + 1];
        if (nextInput) nextInput.focus();
      }
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      const prevInput = inputRefs.current[index - 1];
      if (prevInput) {
        prevInput.focus();
        const newOtp = [...otp];
        newOtp[index - 1] = "";
        setOtp(newOtp);
      }
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text/plain").trim();

    if (/^\d{6}$/.test(pastedData)) {
      const digits = pastedData.split("");
      const newOtp = [...otp];

      digits.slice(0, 6).forEach((digit, index) => {
        newOtp[index] = digit;
      });

      setOtp(newOtp);

      const lastInput = inputRefs.current[Math.min(5, digits.length - 1)];
      if (lastInput) lastInput.focus();
    } else {
      toast.error("Please paste exactly 6 digits");
    }
  };

  const handleResend = async () => {
    if (!email) {
      toast.error("Please enter your email address first");
      return;
    }

    if (countdown > 0) {
      toast.error(
        `Please wait ${countdown} seconds before requesting a new code`,
      );
      return;
    }

    setIsResending(true);
    try {
      const response = await authService.forgotPassword(email);
      if (response.success) {
        toast.success("New OTP sent successfully!");
        setCountdown(60);
      } else {
        toast.error(response.message || "Failed to resend OTP");
      }
    } catch (error: any) {
      if (
        error?.response?.data?.error &&
        !error.response.data.error.includes("session")
      ) {
        toast.error(error.response.data.error || "Failed to resend OTP");
      }
    } finally {
      setIsResending(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const otpString = otp.join("");

    if (!email) {
      toast.error("Please enter your email address");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error("Please enter a valid email address");
      return;
    }

    if (otpString.length !== 6) {
      toast.error("Please enter all 6 digits of the OTP");
      return;
    }

    if (!/^\d{6}$/.test(otpString)) {
      toast.error("Please enter valid digits (0-9)");
      return;
    }

    if (!password || !confirmPassword) {
      toast.error("Please fill in all password fields");
      return;
    }

    if (password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }

    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    setIsLoading(true);
    try {
      const response = await authService.resetPassword(
        email,
        otpString,
        password,
        confirmPassword,
      );

      if (response.success) {
        toast.success("Password reset successfully!");
        navigate("/login", {
          state: {
            message:
              "Password has been reset. Please login with your new password.",
          },
        });
      } else {
        toast.error(response.message || "Password reset failed");
      }
    } catch (error: any) {
      if (error.response?.data?.errors) {
        Object.values(error.response.data.errors).forEach((err: any) => {
          if (!err[0].toLowerCase().includes("session")) {
            toast.error(err[0]);
          }
        });
      } else if (error.response?.data?.error) {
        if (!error.response.data.error.toLowerCase().includes("session")) {
          toast.error(error.response.data.error);
        }
      } else {
        toast.error("An error occurred. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const passwordStrength = (pass: string) => {
    let strength = 0;
    if (pass.length >= 8) strength++;
    if (/[A-Z]/.test(pass)) strength++;
    if (/[a-z]/.test(pass)) strength++;
    if (/[0-9]/.test(pass)) strength++;
    if (/[^A-Za-z0-9]/.test(pass)) strength++;
    return strength;
  };

  const strength = passwordStrength(password);
  const strengthColors = [
    "from-red-500 to-red-400",
    "from-orange-500 to-orange-400",
    "from-yellow-500 to-yellow-400",
    "from-green-400 to-green-300",
    "from-green-600 to-green-500",
  ];

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 via-gray-100 to-gray-200 dark:from-gray-900 dark:via-gray-800 dark:to-gray-950 p-4">
      <div className="w-full max-w-md">
        {/* Back Button */}
        <div className="mb-6">
          <Link
            to="/login"
            className="inline-flex items-center text-sm text-primary hover:text-primary/80 transition-colors group/back"
          >
            <ArrowLeft className="h-4 w-4 mr-2 group-hover/back:-translate-x-1 transition-transform" />
            Back to login
          </Link>
        </div>

        {/* Floating Card */}
        <div className="relative">
          {/* Background Glow */}
          <div className="absolute -inset-4 bg-gradient-to-r from-primary/10 via-primary/5 to-transparent rounded-3xl blur-xl opacity-70"></div>

          {/* Main Card */}
          <div className="relative bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-2xl shadow-2xl border border-gray-200/30 dark:border-gray-700/30">
            {/* Header */}
            <div className="text-center p-6 pb-8">
              <div className="flex justify-center mb-4">
                <div className="relative">
                  <img
                    src={logo}
                    alt="ObeliskRecovery"
                    className="h-10 w-auto"
                  />
                  <div className="absolute -top-1 -right-1 h-2 w-2 bg-green-500 rounded-full animate-pulse"></div>
                </div>
              </div>
              <h1 className="text-2xl font-bold bg-gradient-to-r from-primary to-primary/80 bg-clip-text text-transparent">
                Reset Password
              </h1>
              <p className="text-gray-500 dark:text-gray-400 mt-2 text-sm">
                Enter OTP and set new password
              </p>
            </div>

            {/* Form */}
            <div className="px-6 pb-6">
              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Email Input */}
                <div className="group">
                  <label
                    htmlFor="email"
                    className={`${GlobalStyles.inputLabel} mb-2 flex items-center gap-1`}
                  >
                    <span>Email Address</span>
                    <Sparkles className="h-3 w-3 text-primary/60" />
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none z-10">
                      <Mail className="h-4 w-4 text-primary group-focus-within:text-primary/80 transition-colors" />
                    </div>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className={`${GlobalStyles.input} pl-10 focus:pl-11 transition-all duration-300`}
                      placeholder="Enter your email"
                      disabled={isLoading || !!locationEmail}
                    />
                  </div>
                </div>

                {/* OTP Input */}
                <div className="group">
                  <label
                    className={`${GlobalStyles.inputLabel} mb-2 flex items-center gap-1`}
                  >
                    <span>Verification Code</span>
                    <Sparkles className="h-3 w-3 text-primary/60" />
                  </label>
                  <div
                    className="flex justify-center gap-2 sm:gap-3 mt-2"
                    onPaste={handlePaste}
                  >
                    {otp.map((digit, index) => (
                      <input
                        key={index}
                        ref={(el) => {
                          inputRefs.current[index] = el;
                        }}
                        id={`otp-${index}`}
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        maxLength={1}
                        value={digit}
                        onChange={(e) => handleOtpChange(index, e.target.value)}
                        onKeyDown={(e) => handleKeyDown(index, e)}
                        className={`${GlobalStyles.input} w-10 h-12 sm:w-12 text-center text-xl font-semibold !p-0 focus:ring-2 focus:ring-primary/50 transition-all duration-300`}
                        autoFocus={index === 0}
                        disabled={isLoading}
                        autoComplete="one-time-code"
                      />
                    ))}
                  </div>
                  <div className="flex justify-between items-center mt-2">
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Enter the 6-digit code
                    </p>
                    <button
                      type="button"
                      onClick={handleResend}
                      disabled={isResending || countdown > 0 || !email}
                      className={`text-xs text-primary hover:text-primary/80 transition-colors ${
                        !email ? "opacity-50 cursor-not-allowed" : ""
                      }`}
                    >
                      {isResending ? (
                        <>
                          <div
                            className={`${GlobalStyles.spinner} w-2 h-2 mr-1 inline-block`}
                          ></div>
                          Sending...
                        </>
                      ) : countdown > 0 ? (
                        `Resend in ${countdown}s`
                      ) : (
                        "Resend code"
                      )}
                    </button>
                  </div>
                </div>

                {/* New Password */}
                <div className="group">
                  <label
                    htmlFor="password"
                    className={`${GlobalStyles.inputLabel} mb-2 flex items-center gap-1`}
                  >
                    <span>New Password</span>
                    <Sparkles className="h-3 w-3 text-primary/60" />
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none z-10">
                      <Lock className="h-4 w-4 text-primary group-focus-within:text-primary/80 transition-colors" />
                    </div>
                    <input
                      id="password"
                      name="password"
                      type={showPassword ? "text" : "password"}
                      autoComplete="new-password"
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className={`${GlobalStyles.input} pl-10 pr-10 focus:pl-11 transition-all duration-300`}
                      placeholder="Create new password"
                      disabled={isLoading}
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-primary hover:text-primary/70 transition-colors"
                      onClick={() => setShowPassword(!showPassword)}
                      disabled={isLoading}
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>

                  {/* Password Strength */}
                  {password && (
                    <div className="mt-2 space-y-1">
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-gray-600 dark:text-gray-400">
                          Strength: {strength === 0 && "Very Weak"}
                          {strength === 1 && "Weak"}
                          {strength === 2 && "Fair"}
                          {strength === 3 && "Good"}
                          {strength === 4 && "Strong"}
                          {strength === 5 && "Very Strong"}
                        </span>
                        <div className="flex gap-0.5">
                          {[...Array(5)].map((_, i) => (
                            <div
                              key={i}
                              className={`w-1.5 h-1.5 rounded-full ${
                                i < strength
                                  ? `bg-gradient-to-r ${strengthColors[i]}`
                                  : "bg-gray-300 dark:bg-gray-700"
                              }`}
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Confirm Password */}
                <div className="group">
                  <label
                    htmlFor="confirmPassword"
                    className={`${GlobalStyles.inputLabel} mb-2`}
                  >
                    Confirm New Password
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none z-10">
                      <Lock className="h-4 w-4 text-primary group-focus-within:text-primary/80 transition-colors" />
                    </div>
                    <input
                      id="confirmPassword"
                      name="confirmPassword"
                      type={showConfirmPassword ? "text" : "password"}
                      autoComplete="new-password"
                      required
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className={`${GlobalStyles.input} pl-10 pr-10 focus:pl-11 transition-all duration-300 ${
                        confirmPassword && password !== confirmPassword
                          ? "border-red-500 dark:border-red-500"
                          : confirmPassword && password === confirmPassword
                            ? "border-green-500 dark:border-green-500"
                            : ""
                      }`}
                      placeholder="Confirm new password"
                      disabled={isLoading}
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-primary hover:text-primary/70 transition-colors"
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                      disabled={isLoading}
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                  {confirmPassword && password !== confirmPassword && (
                    <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                      Passwords do not match
                    </p>
                  )}
                  {confirmPassword && password === confirmPassword && (
                    <div className="mt-1 flex items-center gap-1 text-xs text-green-600 dark:text-green-400">
                      <CheckCircle className="h-3 w-3" />
                      <span>Passwords match</span>
                    </div>
                  )}
                </div>

                {/* Submit Button */}
                <div className="pt-2">
                  <button
                    type="submit"
                    disabled={
                      isLoading ||
                      otp.join("").length !== 6 ||
                      password !== confirmPassword ||
                      password.length < 8 ||
                      !email.includes("@")
                    }
                    className={`${GlobalStyles.button} ${GlobalStyles.buttonPrimary} ${GlobalStyles.buttonMedium} w-full group/btn ${
                      otp.join("").length !== 6 ||
                      password !== confirmPassword ||
                      password.length < 8 ||
                      !email.includes("@")
                        ? "opacity-50 cursor-not-allowed"
                        : ""
                    }`}
                  >
                    {isLoading ? (
                      <div className="flex items-center justify-center">
                        <div
                          className={`${GlobalStyles.spinner} w-4 h-4 mr-2 border-2`}
                        ></div>
                        Resetting Password...
                      </div>
                    ) : (
                      <div className="flex items-center justify-center">
                        <span>Reset Password</span>
                        <svg
                          className="w-4 h-4 ml-2 opacity-0 group-hover/btn:opacity-100 transition-all duration-300 transform group-hover/btn:translate-x-1"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      </div>
                    )}
                  </button>
                </div>
              </form>

              {/* Security Requirements */}
              <div className="mt-6 p-3 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800/30 dark:to-gray-900/30 rounded-lg border border-gray-200/50 dark:border-gray-700/50">
                <div className="flex items-start gap-2">
                  <Shield className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Password Requirements:
                    </p>
                    <ul className="text-xs text-gray-600 dark:text-gray-400 space-y-0.5">
                      <li className="flex items-center gap-1">
                        <CheckCircle className="h-3 w-3 text-green-500 flex-shrink-0" />
                        <span>At least 8 characters</span>
                      </li>
                      <li className="flex items-center gap-1">
                        <CheckCircle className="h-3 w-3 text-green-500 flex-shrink-0" />
                        <span>Uppercase & lowercase letters</span>
                      </li>
                      <li className="flex items-center gap-1">
                        <CheckCircle className="h-3 w-3 text-green-500 flex-shrink-0" />
                        <span>Numbers and special characters</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
